import React, { useRef, useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { useIsMobile } from '@/hooks/use-mobile';

interface Category {
  id: string;
  name: string;
  count?: number;
}

interface CategoryFilterProps {
  categories: Category[];
  selectedCategory: string | null;
  onSelectCategory: (categoryId: string | null) => void;
  className?: string;
  isLoading?: boolean;
  isFiltering?: boolean;
}

const CategoryFilter = ({
  categories,
  selectedCategory,
  onSelectCategory,
  className,
  isLoading = false,
  isFiltering = false,
}: CategoryFilterProps) => {
  const isMobile = useIsMobile();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showScrollButtons, setShowScrollButtons] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Check if scroll buttons should be shown
  useEffect(() => {
    const checkScroll = () => {
      if (!scrollContainerRef.current) return;
      
      const { scrollWidth, clientWidth, scrollLeft } = scrollContainerRef.current;
      setShowScrollButtons(scrollWidth > clientWidth);
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1); // 1px buffer for rounding errors
    };

    checkScroll();
    window.addEventListener('resize', checkScroll);
    
    return () => window.removeEventListener('resize', checkScroll);
  }, [categories]);

  // Update scroll indicators when scrolling
  const handleScroll = () => {
    if (!scrollContainerRef.current) return;
    
    const { scrollWidth, clientWidth, scrollLeft } = scrollContainerRef.current;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
  };

  // Scroll left/right button handlers
  const scrollLeft = () => {
    if (!scrollContainerRef.current) return;
    scrollContainerRef.current.scrollBy({ left: -200, behavior: 'smooth' });
  };

  const scrollRight = () => {
    if (!scrollContainerRef.current) return;
    scrollContainerRef.current.scrollBy({ left: 200, behavior: 'smooth' });
  };

  // Show loading skeleton when loading
  if (isLoading) {
    return (
      <div className={cn("w-full", className)}>
        <div className="flex space-x-2 py-4 px-1">
          <Skeleton className="h-10 w-16 rounded-lg" />
          <Skeleton className="h-10 w-20 rounded-lg" />
          <Skeleton className="h-10 w-16 rounded-lg" />
          <Skeleton className="h-10 w-18 rounded-lg" />
          <Skeleton className="h-10 w-24 rounded-lg" />
          <Skeleton className="h-10 w-14 rounded-lg" />
        </div>
      </div>
    );
  }

  return (
    <div className={cn("relative w-full", className)}>
      {showScrollButtons && !isMobile && (
        <>
          <button 
            onClick={scrollLeft}
            disabled={isFiltering}
            className={cn(
              "absolute left-0 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-8 h-8 rounded-full bg-background/80 shadow-sm text-foreground transition-opacity hover:bg-background/90", 
              canScrollLeft && !isFiltering ? "opacity-100" : "opacity-0 pointer-events-none"
            )}
            aria-label="Scroll left"
          >
            <ArrowLeft size={16} />
          </button>
          <button 
            onClick={scrollRight}
            disabled={isFiltering}
            className={cn(
              "absolute right-0 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-8 h-8 rounded-full bg-background/80 shadow-sm text-foreground transition-opacity hover:bg-background/90", 
              canScrollRight && !isFiltering ? "opacity-100" : "opacity-0 pointer-events-none"
            )}
            aria-label="Scroll right"
          >
            <ArrowRight size={16} />
          </button>
        </>
      )}
      
      <div 
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className={cn(
          "w-full overflow-x-auto py-4 scrollbar-hide",
          isMobile ? "scroll-smooth" : ""
        )}
        style={{ 
          scrollbarWidth: 'none',
          msOverflowStyle: 'none'
        }}
      >
        <div className={cn(
          "flex space-x-2 min-w-max pb-1",
          isMobile ? "px-2" : "px-1"
        )}>
          <button
            onClick={() => onSelectCategory(null)}
            disabled={isFiltering}
            className={cn(
              "px-4 py-2 text-sm rounded-lg transition-all relative whitespace-nowrap",
              isMobile && "min-h-[44px] px-6",
              selectedCategory === null
                ? "bg-primary text-primary-foreground shadow-sm"
                : "bg-secondary text-secondary-foreground hover:bg-secondary/80",
              isFiltering && "opacity-50 cursor-not-allowed"
            )}
          >
            All
            {isFiltering && selectedCategory === null && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              </div>
            )}
          </button>
          
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => onSelectCategory(category.id)}
              disabled={isFiltering}
              className={cn(
                "px-4 py-2 text-sm rounded-lg transition-all relative whitespace-nowrap",
                isMobile && "min-h-[44px] px-6",
                selectedCategory === category.id
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "bg-secondary text-secondary-foreground hover:bg-secondary/80",
                isFiltering && "opacity-50 cursor-not-allowed"
              )}
            >
              <span className={cn(isFiltering && selectedCategory === category.id && "opacity-0")}>
                {category.name}
                {category.count !== undefined && (
                  <span className="ml-1.5 px-1.5 py-0.5 text-xs rounded-full bg-background/20">
                    {category.count}
                  </span>
                )}
              </span>
              {isFiltering && selectedCategory === category.id && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                </div>
              )}
            </button>
          ))}
        </div>
      </div>
      
      {isFiltering && (
        <div className="text-center text-sm text-muted-foreground mt-2">
          Filtering solutions...
        </div>
      )}
    </div>
  );
};

export default CategoryFilter;
